import * as S from './styles';
import { useClient, useTables } from '@/hooks';
import { useSyncOnDemand } from '@/hooks/useSyncOnDemand';
import { getOnStorage } from '@/shared';
import { copy } from '@/shared/commonFunctions';
import { ClientProps, TabCodes, TabParams } from '@/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListCompany, Modal, TabSlider, DependenciesClient } from '@/components';
import { addDays, isBefore } from 'date-fns';

interface Props {
  children: any;
  forceReload?: boolean;
}

const ModalListCompanies = ({ children, forceReload = false }: Props) => {
  const { tables } = useTables();
  const { allClients, campanhaClientes } = useClient();
  const { isSyncing, syncIfNeeded } = useSyncOnDemand();

  const isLoading = useRef(false);
  const hasSyncedForModal = useRef(false);
  const eventos = tables.current.eventos;
  const [filter, setFilter] = useState('');
  const [isOpenedModal, openModal] = useState(false);
  const [localClient, setLocalClient] = useState(() => {
    return getOnStorage('selected_client') as ClientProps;
  });
  const [selectedTab, setSelectedTab] = useState('all' as TabCodes);

  const loadcurrentClient = useCallback(() => {
    const currClient = getOnStorage('selected_client');

    if (currClient) setLocalClient(currClient);
    isLoading.current = false;
  }, []);

  const setClient = useCallback(
    (client: ClientProps | undefined) => {
      if (!client) return;
      setLocalClient(client);
    },
    [setLocalClient]
  );

  /**
   * GARANTE SINCRONIZAÇÃO QUANDO MODAL ABRE SEM DADOS DE CLIENTES
   * Reutiliza infraestrutura existente via useSyncOnDemand
   * Caso não haja dados de clientes, força sincronização
   */
  const handleModalOpen = useCallback(async () => {
    openModal(true);

    // Se não há dados de clientes, força sincronização
    if (!allClients?.length && !hasSyncedForModal.current && !isSyncing) {
      console.log('ModalListCompanies: Sem dados de clientes - forçando sincronização...');
      hasSyncedForModal.current = true;

      const syncSuccess = await syncIfNeeded({
        requiredTables: ['cliente', 'campanhaClientes', 'eventos'],
        force: false, // Só sincroniza se realmente não há dados
      });

      if (!syncSuccess) {
        console.error('ModalListCompanies: Falha na sincronização de dados de clientes');
      } else {
        console.log('ModalListCompanies: Dados de clientes sincronizados com sucesso');
      }
    }
  }, [allClients?.length, isSyncing, syncIfNeeded]);

  useEffect(() => {
    if (allClients?.length || isLoading.current) return;

    isLoading.current = true;
    loadcurrentClient();
  }, [loadcurrentClient, allClients, isLoading]);

  // Reset do flag quando dados chegam
  useEffect(() => {
    if (allClients?.length > 0) {
      hasSyncedForModal.current = false;
    }
  }, [allClients?.length]);

  const listTabs: TabParams[] = useMemo(
    () => [
      {
        disabled: false,
        code: 'all',
        title: 'Todos',
        notification: allClients?.length,
      },
      {
        disabled: false,
        code: 'tickets',
        title: 'Boletos',
        notification: allClients?.filter((c) => eventos?.[c.COD_CLIENTE])?.length || 0,
      },
      {
        disabled: false,
        code: 'activeTickets',
        title: 'Boletos Vencidos',
        notification:
          allClients?.filter((c) =>
            eventos?.[c.COD_CLIENTE]?.boletos?.some((b) => b.parcelas?.some((p) => p.status === 'VENCIDO'))
          )?.length || 0,
      },
      {
        disabled: false,
        code: 'inactiveTickets',
        title: 'Boletos a Vencer',
        notification:
          allClients?.filter((c) =>
            eventos?.[c.COD_CLIENTE]?.boletos?.some((b) =>
              b.parcelas?.some((p) => p.status === 'ABERTO' && isBefore(p.dt_vencimento, addDays(new Date(), 5)))
            )
          )?.length || 0,
      },
      {
        disabled: false,
        code: 'active',
        title: 'Ativos',
        notification: allClients?.filter((c) => c.ST_COMPRA != 'I').length || 0,
      },
      {
        disabled: false,
        code: 'blocked',
        title: 'Bloqueados',
        notification: allClients?.filter((c) => c.ST_COMPRA === 'I').length || 0,
      },
      {
        disabled: !allClients?.filter((c) => c.daysWithoutPurchase).length,
        code: 'noPurchases',
        title: 'Sem comprar',
        notification: allClients?.filter((c) => c.daysWithoutPurchase).length || 0,
      },
      {
        disabled: false,
        code: 'approved',
        title: 'Positivou na Campanha',
        notification: allClients?.filter((c) => {
          const ativos = campanhaClientes?.ativo?.clientes || [];
          return ativos?.includes(c.COD_CLIENTE);
        }).length,
      },
      {
        disabled: false,
        code: 'notApproved',
        title: 'Não positivou na campanha',
        notification: allClients?.filter((c) => {
          const inativos = campanhaClientes?.inativo?.cliente || [];
          return inativos?.includes(c.COD_CLIENTE);
        }).length,
      },
      // {
      //   disabled: true,
      //   code: "titles",
      //   title: "Títulos",
      //   notification: clients.filter((c) => c.notifications).length,
      // }
    ],
    [allClients, campanhaClientes]
  );

  const listClients = useMemo((): ClientProps[] => {
    let filteredList: ClientProps[] = [];
    if (filter) {
      filteredList =
        allClients?.filter((c) => {
          const toFilter = copy(c);

          delete toFilter.progress1;
          delete toFilter.progress2;
          delete toFilter.progress3;
          delete toFilter.progress4;
          delete toFilter.COD_PAGAMENTO;

          return Object.values(toFilter).toString().toLowerCase().includes(filter.toLocaleLowerCase());
        }) || [];
    } else if (selectedTab === 'all') {
      filteredList = allClients || [];
    } else if (selectedTab === 'tickets') {
      filteredList = allClients?.filter((c) => eventos?.[c.COD_CLIENTE]) || [];
    } else if (selectedTab === 'activeTickets') {
      filteredList =
        allClients?.filter((c) =>
          eventos?.[c.COD_CLIENTE]?.boletos?.some((b) => b.parcelas?.some((p) => p.status === 'VENCIDO'))
        ) || [];
    } else if (selectedTab === 'inactiveTickets') {
      filteredList =
        allClients?.filter((c) =>
          eventos?.[c.COD_CLIENTE]?.boletos?.some((b) =>
            b.parcelas?.some((p) => p.status === 'ABERTO' && isBefore(p.dt_vencimento, addDays(new Date(), 5)))
          )
        ) || [];
    } else if (selectedTab === 'noPurchases') {
      filteredList = allClients?.filter((c) => c.daysWithoutPurchase >= 30) || [];
    } else if (selectedTab === 'suspended') {
      filteredList = allClients?.filter((c) => c.suspended) || [];
    } else if (selectedTab === 'blocked') {
      filteredList = allClients?.filter((c) => c.ST_COMPRA === 'I') || [];
    } else if (selectedTab === 'active') {
      filteredList = allClients?.filter((c) => c.ST_COMPRA !== 'I') || [];
    } else if (selectedTab === 'notApproved') {
      filteredList =
        allClients?.filter((c) => {
          const inativos = campanhaClientes?.inativo?.cliente || [];
          return inativos?.includes(c.COD_CLIENTE);
        }) || [];
    } else if (selectedTab === 'approved') {
      filteredList =
        allClients?.filter((c) => {
          const ativos = campanhaClientes?.ativo?.clientes || [];
          return ativos?.includes(c.COD_CLIENTE);
        }) || [];
    } else {
      filteredList = allClients?.filter((c) => c.notifications) || [];
    }
    return filteredList;
  }, [filter, allClients, campanhaClientes, selectedTab]);

  const isEnabled = useMemo(() => {
    return !!localClient?.COD_CLIENTE;
  }, [localClient?.COD_CLIENTE]);

  const { selectedClient } = useClient();

  useEffect(() => {
    if (listClients?.length === 0 || !selectedClient?.COD_CLIENTE || !isOpenedModal) return;

    const listCompaniesContent = document.getElementById('list-companies-content') as HTMLDivElement;
    const scrollToSelectedClient = () => {
      const selectedClientElement = document.getElementById(`client-${selectedClient?.COD_CLIENTE}`) as HTMLElement;

      if (listCompaniesContent && selectedClientElement) {
        const containerRect = listCompaniesContent.getBoundingClientRect();
        const elementRect = selectedClientElement.getBoundingClientRect();
        const distanceToTop = elementRect.top - containerRect.top + listCompaniesContent.scrollTop - 20;

        listCompaniesContent.scrollTo({ top: distanceToTop, behavior: 'smooth' });
      }
    };

    setTimeout(scrollToSelectedClient, 1000);
  }, [isOpenedModal]);

  return (
    <>
      <S.ButtonSpan onClick={handleModalOpen} data-test="modal-companies-button-open">
        {children}
      </S.ButtonSpan>

      <Modal fullHeight id="list-companies" isOpen={isOpenedModal} closeModal={openModal} isScrollable={true}>
        <S.ModalBody>
          {/* Adicione este wrapper se ele não existir */}
          <S.ModalTitle>{isSyncing ? 'Sincronizando dados dos clientes...' : 'Para iniciar, selecione um cliente'}</S.ModalTitle>
          <S.InputContent>
            <S.SearchIcon $isgray size={24} />

            <S.InputModal
              value={filter}
              placeholder="Busque por cliente"
              data-test="modal-list-companies-input-search"
              onChange={(event: any) => setFilter(event.target.value.trim())}
            />
          </S.InputContent>
          <S.ContentTabSlider>
            <TabSlider lightMode listTabs={listTabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
          </S.ContentTabSlider>
          <S.ContentListCompany id="list-companies-content">
            <ListCompany
              clients={listClients}
              selectedClient={localClient}
              setLocalClient={setClient}
              isModalOpen={isOpenedModal}
            />
          </S.ContentListCompany>
          {isEnabled && isOpenedModal && (
            <DependenciesClient
              forceReload={forceReload}
              selectedClient={localClient}
              cbFunction={() => {
                openModal(false);
              }}
            />
          )}
        </S.ModalBody>
        {/* Feche o wrapper */}
      </Modal>
    </>
  );
};

export default ModalListCompanies;
